import React, { useState } from "react";
import { motion } from "framer-motion";
import "./ProjectListItem.css";
import PostAddIcon from '@mui/icons-material/PostAdd';
import { PostAdd, Download, EditNote, Visibility } from "@mui/icons-material";
import Tooltip from "@mui/material/Tooltip";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from "react-router-dom";
import Link from '@mui/material/Link';
import { FinalEvaluation, Proyecto } from "api/projectService";
import { generateWordDocument, getRadarData, RadarOptions, descargarPdf } from "api/documentService";
import { CircularProgress } from "@mui/material";
import { Radar } from "react-chartjs-2";

/** Tipos de ejemplo */
interface Evaluation {
    id: string;
    name: string;
    activa: boolean;
    brl: number; trl: number; iprl: number; tmrl: number; frl: number; crl:number;
    // ... otras propiedades
}

interface Project {
    id: string;
    title: string;
    description: string;
    owner: string;
    originalProject: Proyecto;
    evaluations: Evaluation[];
}

interface ProjectListItemProps {
    project: Project;
}

/**
 * Muestra un proyecto con:
 *  - Tres propiedades en la cabecera: title, description, owner
 *  - Lista colapsable de evaluaciones: solo dos visibles, botón para expandir/colapsar
 */
export default function ProjectListItem({ project }: ProjectListItemProps) {
    const [showAllEvaluations, setShowAllEvaluations] = useState(false);
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)

    // Se decide cuántas evaluaciones mostrar
    const visibleEvaluations = showAllEvaluations
        ? project.evaluations
        : project.evaluations.slice(0, 2);

    const hasMoreEvaluations = project.evaluations.length > 2;

    const toggleShowAll = () => {
        setShowAllEvaluations(!showAllEvaluations);
    };

    return (
        
            
            <div className="projectCard">
                {/* Cabecera: 3 propiedades del proyecto */}
                <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                    className="headerRow"
                >
                    {/* 1) Título del proyecto */}
                    <div className="title">Proyecto: {project.title}</div>
                    {/* 2) Descripción */}
                    <div className="description">{project.description}</div>
                    {/* 3) Propietario */}
                    <div className="owner">

                        {/*<Link className="addEvaluacionButton" href="/InformeRL.pdf" download="InformeRL.pdf">*/}
                        {/*<Link className="addEvaluacionButton" onClick={() => generateWordDocument(project.originalProject.evaluaciones[0])}>*/}

                        {project.originalProject.evaluaciones?.some(eva => !eva.activa) ?
                            <Tooltip title="Descargar informe">
                                <Link className="addEvaluacionButton" onClick={async () => {
                                    try {
                                        const blobOrBase64 = await descargarPdf(project.originalProject.idProyecto);
                                        if (blobOrBase64) {
                                            const textoInicial = await blobOrBase64.slice(0, 30).text();
                                            if (textoInicial.startsWith("%PDF")) {
                                                const downloadUrl = window.URL.createObjectURL(blobOrBase64);
                                                const link = document.createElement("a");
                                                link.href = downloadUrl;
                                                const fechaActual = new Date().toLocaleDateString("es-CL", { day: "2-digit", month: "2-digit", year: "numeric" });

                                                link.download = `Informe_${project.originalProject.nombre}_${fechaActual}.pdf`;
                                                document.body.appendChild(link);
                                                link.click();
                                                document.body.removeChild(link);
                                            } else {
                                                console.log("Parece ser Base64");
                                                // Si es una cadena, se asume que es Base64
                                                const texto = await blobOrBase64.text();
                                                console.log(texto);
                                                const base64data = (texto + "").startsWith("data:application/pdf;base64,")
                                                    ? texto // Si ya tiene el prefijo, lo usamos directamente
                                                    : `data:application/pdf;base64,${texto}`; // Agregar el prefijo si es necesario

                                                const link = document.createElement("a");
                                                link.href = base64data;
                                                const fechaActual = new Date().toLocaleDateString("es-CL", { day: "2-digit", month: "2-digit", year: "numeric" });

                                                link.download = `Informe_${project.originalProject.nombre}_${fechaActual}.pdf`;
                                                document.body.appendChild(link);
                                                link.click();
                                                document.body.removeChild(link);
                                            }
                                        } else {
                                            console.error("Error al descargar documento, servicio no devuelve data");
                                            alert("Error al descargar documento. Por favor intente otra vez más tarde.");
                                        }
                                    } catch (error) {
                                        console.error(error);
                                        alert("Error al descargar documento. Por favor intente otra vez más tarde.");
                                    } finally {
                                        setLoading(false);
                                    }
                                }}>

                                    {loading ? <CircularProgress size={24} color="inherit" /> : (<Download>+</Download>)}
                                </Link>
                            </Tooltip>
                            :
                            ''
                        }
                        <Tooltip title="Nueva evaluación">
                            <Link className="addEvaluacionButton" onClick={() => {
                                navigate(`/evaluacion`, { state: { project } });
                            }}>
                                <PostAdd>+</PostAdd>
                            </Link>
                        </Tooltip>
                    </div>
                </motion.div>

                {/* Lista de evaluaciones */}
                <div className={project.originalProject.imagenRadar ? "evaluationsContainerWrapper2Col" : "evaluationsContainerWrapper1Col"}>

                    <div>

                        <div className="evaluationsContainer">
                            {visibleEvaluations.map((evaluation) => (
                                <div>
                                    <motion.div
                                        key={evaluation.id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.3 }}
                                        className="evaluationCard"
                                    >
                                        <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                                            <div>
                                                <h3 className="evaluationTitle">
                                                    Evaluación: {evaluation.name}
                                                </h3>
                                            </div>
                                            <div>
                                                <h3 className="evaluationTitle">
                                                    <Link className="addEvaluacionButton" onClick={() => {
                                                        project.originalProject.nuevaEvaluacion = project.originalProject.evaluaciones.find(e => (e.id + "") === evaluation.id) ?? null;
                                                        navigate(`/evaluacion`, { state: { project } });
                                                    }}>
                                                        {
                                                            evaluation.activa ? <Tooltip title="Completar"><EditNote></EditNote></Tooltip> : <Tooltip title="Ver detalle"><Visibility></Visibility></Tooltip>
                                                        }

                                                    </Link>
                                                </h3>


                                            </div>
                                        </div>

                                        {/* Aquí podrías renderizar tu formulario de preguntas, si lo deseas */}
                                        <p className="evaluationText">
                                            CRL: <strong>{evaluation.crl}</strong> BRL: <strong>{evaluation.brl}</strong>   TRL: <strong>{evaluation.trl}</strong>   IPRL: <strong>{evaluation.iprl}</strong>   TmRL: <strong>{evaluation.tmrl}</strong>   FRL: <strong>{evaluation.frl}</strong>
                                        </p>
                                    </motion.div>
                                </div>
                            ))}
                        </div>

                        {hasMoreEvaluations && (
                            <div className="showMoreButtonContainer">
                                <button onClick={toggleShowAll} className="showMoreButton" >
                                    {showAllEvaluations ? "Ver menos" : "Ver más"}
                                </button>
                            </div>
                        )}
                    </div>
                    {project.originalProject.imagenRadar ?
                        <div className="div2Container" >

                            {
                                <img
                                    src={`data:image/jpeg;base64,${project.originalProject.imagenRadar.replace('dataimage/jpegbase64', '')}`}
                                    width={'484'}
                                    alt={`${project.originalProject.nombre}`}
                                />
                            }
                        </div>
                        :
                        <></>
                    }
                </div>
            </div>
        

    )
}

